import React from 'react';
import { X } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';

type ThumbnailItem = {
  id: string;
  src: string;
  alt?: string;
  url?: string;
  status?: 'uploading' | 'uploaded';
};

const sizeConfig = {
  xs: { container: 'size-12', spinner: 'size-3', close: 'top-0.5 right-0.5 p-0.5', closeIcon: 'size-2' },
  sm: { container: 'size-16', spinner: 'size-4', close: 'top-0.5 right-0.5 p-0.5', closeIcon: 'size-2.5' },
  md: { container: 'size-20', spinner: 'size-5', close: 'top-1 right-1 p-1', closeIcon: 'size-3' },
} as const;

interface AttachmentThumbnailsProps {
  items: ThumbnailItem[];
  size?: keyof typeof sizeConfig;
  onRemove?: (id: string) => void;
  className?: string;
}

export function AttachmentThumbnails({ items, size = 'sm', onRemove, className }: AttachmentThumbnailsProps) {
  if (items.length === 0) return null;

  const config = sizeConfig[size];

  return (
    <div className={cn('flex gap-2 overflow-x-auto', className)}>
      {items.map((item) => (
        <div key={item.id} className={cn('relative group rounded-lg overflow-hidden flex-shrink-0', config.container)}>
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(!item.url && 'pointer-events-none')}
          >
            <img src={item.src} alt={item.alt || 'Attachment'} className="w-full h-full object-cover" />
          </a>
          {item.status === 'uploading' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <div
                className={cn(
                  'animate-spin rounded-full border-[2.5px] border-black/30 border-t-white',
                  config.spinner,
                )}
              />
            </div>
          )}
          {onRemove && (
            <button
              onClick={() => onRemove(item.id)}
              className={cn(
                'absolute rounded-full opacity-0',
                'group-hover:opacity-100 transition-opacity duration-200',
                'hover:bg-black/70 bg-black/50',
                config.close,
              )}
            >
              <X className={cn('text-white', config.closeIcon)} aria-label="Remove attachment" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
